/**
 * PlazaPagination.jsx — shared pagination + view-mode + page-size UI
 * used by every Plaza-family storefront (HustlePlaza, Tackle, HustleShop,
 * and any future shopfronts).
 *
 * The user's spec (Feb 2026):
 *   • Items-per-page selector: **top-right** of the grid
 *   • Grid / List toggle: **top-right** alongside page size
 *   • Bottom pagination:
 *       - List mode → industry-standard numbered navigator:
 *           « ‹ 1 2 3 … 42 › »  Last
 *       - Grid mode → "Load more" button (infinite-scroll style)
 *   • "Showing X of Y" line above pagination in both modes
 *
 * Design goal: drop-in — pass state + setters, get consistent chrome.
 * Zero coupling to any single storefront's business logic.
 */
import { useMemo } from "react";
import { List as ListIcon, LayoutGrid, ChevronLeft, ChevronRight,
         ChevronsLeft, ChevronsRight, Loader2 } from "lucide-react";
import "./PlazaPagination.css";

/**
 * Compute the elided page-number list.
 *   e.g. 42 pages, current 7 → [1, "…", 5, 6, 7, 8, 9, "…", 42]
 */
function pageWindow(current, total, edge = 1, siblings = 2) {
  if (total <= 7 + edge * 2) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out = [];
  const left  = Math.max(edge + 1, current - siblings);
  const right = Math.min(total - edge, current + siblings);
  for (let i = 1; i <= edge; i++) out.push(i);
  if (left > edge + 1) out.push("…");
  for (let i = left; i <= right; i++) out.push(i);
  if (right < total - edge) out.push("…");
  for (let i = total - edge + 1; i <= total; i++) out.push(i);
  return out;
}

export default function PlazaPagination({
  // View toggle
  view,            // "list" | "grid"
  onView,          // (next) => void
  showViewToggle = true,

  // Page-size selector
  pageSize,
  onPageSize,      // (n) => void
  pageSizeOptions, // number[]

  // "Showing X of Y" counters
  shown,           // items currently rendered
  total,           // total live count in catalog
  countLabel,     // optional override i18n label

  // List-mode numbered navigator
  page,            // 1-indexed current page
  totalPages,      // int
  onPage,          // (nextPage) => void

  // Grid-mode load-more
  hasMore,
  loadingMore,
  onLoadMore,

  // Test-ID namespace so multiple instances don't collide
  testidPrefix = "plaza-pagi",

  // i18n — each label is a plain string the caller has already translated.
  // Defaults keep the component usable standalone (e.g. new storefront
  // before someone adds keys to `en.json`).
  labels = {},
}) {
  const L = {
    show:        "Show",
    perPage:     "per page",
    listView:    "List",
    gridView:    "Grid",
    loadMore:    "Load more",
    loading:     "Loading…",
    firstTitle:  "First page",
    prevTitle:   "Previous page",
    nextTitle:   "Next page",
    lastTitle:   "Last page",
    lastLabel:   "Last",
    ...labels,
  };

  const pages = useMemo(
    () => pageWindow(page || 1, totalPages || 1),
    [page, totalPages]
  );

  const goTo = (n) => {
    if (!onPage) return;
    if (n < 1 || n > totalPages || n === page) return;
    onPage(n);
  };

  const shownStr = (shown ?? 0).toLocaleString("en-AU");
  const totalStr = (total ?? 0).toLocaleString("en-AU");

  return (
    <>
      {/* ── Top-right toolbar: view toggle + page-size ─────────── */}
      <div className="pp-toolbar" data-testid={`${testidPrefix}-toolbar`}>
        {showViewToggle && (
          <div className="pp-views" data-testid={`${testidPrefix}-views`}>
            <button
              type="button"
              className={view === "list" ? "is-active" : ""}
              onClick={() => onView && onView("list")}
              title={L.listView}
              data-testid={`${testidPrefix}-view-list`}
              aria-pressed={view === "list"}
            ><ListIcon size={15} /> <span>{L.listView}</span></button>
            <button
              type="button"
              className={view === "grid" ? "is-active" : ""}
              onClick={() => onView && onView("grid")}
              title={L.gridView}
              data-testid={`${testidPrefix}-view-grid`}
              aria-pressed={view === "grid"}
            ><LayoutGrid size={15} /> <span>{L.gridView}</span></button>
          </div>
        )}
        <label className="pp-size" data-testid={`${testidPrefix}-size-wrap`}>
          <span className="pp-size__lbl">{L.show}</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSize && onPageSize(Number(e.target.value))}
            data-testid={`${testidPrefix}-size`}
          >
            {(pageSizeOptions || [25, 50, 100, 250, 500]).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span className="pp-size__unit">{L.perPage}</span>
        </label>
      </div>

      {/* ── Bottom: showing X of Y + numbered nav OR load-more ── */}
      <div className="pp-bottom" data-testid={`${testidPrefix}-bottom`}>
        <div className="pp-count" data-testid={`${testidPrefix}-count`}>
          {countLabel || (
            <>Showing <b>{shownStr}</b> of <b>{totalStr}</b></>
          )}
        </div>

        {view === "grid" ? (
          hasMore ? (
            <button
              type="button"
              className="pp-loadmore"
              disabled={loadingMore}
              onClick={onLoadMore}
              data-testid={`${testidPrefix}-load-more`}
            >
              {loadingMore ? (
                <><Loader2 className="pp-spin" size={14} /> {L.loading}</>
              ) : L.loadMore}
            </button>
          ) : null
        ) : (
          totalPages > 1 && (
            <nav
              className="pp-nav"
              role="navigation"
              aria-label="Pagination"
              data-testid={`${testidPrefix}-nav`}
            >
              <button
                type="button"
                className="pp-nav__btn"
                disabled={page <= 1}
                onClick={() => goTo(1)}
                aria-label={L.firstTitle}
                title={L.firstTitle}
                data-testid={`${testidPrefix}-first`}
              ><ChevronsLeft size={16} /></button>
              <button
                type="button"
                className="pp-nav__btn"
                disabled={page <= 1}
                onClick={() => goTo(page - 1)}
                aria-label={L.prevTitle}
                title={L.prevTitle}
                data-testid={`${testidPrefix}-prev`}
              ><ChevronLeft size={16} /></button>

              {pages.map((n, i) =>
                n === "…" ? (
                  <span
                    key={`ellipsis-${i}`}
                    className="pp-nav__ellipsis"
                    aria-hidden
                  >…</span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    className={
                      n === page
                        ? "pp-nav__num is-active"
                        : "pp-nav__num"
                    }
                    aria-current={n === page ? "page" : undefined}
                    onClick={() => goTo(n)}
                    data-testid={`${testidPrefix}-page-${n}`}
                  >{n}</button>
                )
              )}

              <button
                type="button"
                className="pp-nav__btn"
                disabled={page >= totalPages}
                onClick={() => goTo(page + 1)}
                aria-label={L.nextTitle}
                title={L.nextTitle}
                data-testid={`${testidPrefix}-next`}
              ><ChevronRight size={16} /></button>
              <button
                type="button"
                className="pp-nav__btn pp-nav__last"
                disabled={page >= totalPages}
                onClick={() => goTo(totalPages)}
                aria-label={L.lastTitle}
                title={L.lastTitle}
                data-testid={`${testidPrefix}-last`}
              ><ChevronsRight size={16} /> <span>{L.lastLabel}</span></button>
            </nav>
          )
        )}
      </div>
    </>
  );
}
